// Generated fallback art for MechView: used when art.mechs[id] (or one of its
// textures, a weapon or an equipment module) is missing, so every caller
// (gameplay, hangar, results, preview) always renders a clean, readable mech
// in the right colours instead of crashing. Textures are created once per key
// and shared by every view and scene.

import type Phaser from 'phaser';
import { CELL, FOOT_ANCHOR } from '../../config/constants';
import { COLORS, css, shade } from '../../config/palette';
import type {
  AnimKey,
  AttachKey,
  AttachPoint,
  EquipSlot,
  MechFrameMeta,
  MechId,
  VisualPiece,
  WeaponArt,
} from '../../core/types';
import { MECHS } from '../../data/mechs';
import { solveTwoBone, twoBoneResult } from './ik';

/** Animation source the view drives (real art and placeholders share this shape). */
export interface AnimSrc {
  /** Texture frame per animation frame. */
  frames: number[];
  fps: number;
  loop: boolean;
  meta: MechFrameMeta[];
}

export interface RigSrc {
  bodyKey: string;
  glowKey: string | null;
  anims: Record<AnimKey, AnimSrc>;
  upperKey: string;
  foreKey: string;
  upperGlowKey: string | null;
  foreGlowKey: string | null;
  upperLen: number;
  foreLen: number;
  upperOrigin: { x: number; y: number };
  foreOrigin: { x: number; y: number };
  rearTint: number;
  cell: number;
  footAnchor: { x: number; y: number };
  placeholder: boolean;
}

interface Palette {
  armor: number;
  dark: number;
  light: number;
  reactor: number;
}

function paletteFor(id: MechId): Palette {
  const reactor = MECHS[id]?.reactorColor ?? COLORS.volt;
  switch (id) {
    case 'bastion':
      return { armor: 0x7f776c, dark: 0x4f4943, light: 0xada395, reactor };
    case 'wraith':
      return { armor: 0x5c5e83, dark: 0x35364f, light: 0x8e90b8, reactor };
    default:
      return { armor: 0x8b9cb3, dark: 0x51617a, light: 0xbfcddd, reactor };
  }
}

// ── Placeholder body poses ──

interface LegKey {
  /** Foot x offset from the anchor. */
  x: number;
  /** Sole height above the ground. */
  lift: number;
}
interface PhPose {
  hipY: number;
  lean: number;
  F: LegKey;
  R: LegKey;
}

const PH_POSES: PhPose[] = [
  /* 0 idle a */ { hipY: 84, lean: 0.04, F: { x: 7, lift: 0 }, R: { x: -7, lift: 0 } },
  /* 1 idle b */ { hipY: 85.5, lean: 0.05, F: { x: 7, lift: 0 }, R: { x: -7, lift: 0 } },
  /* 2 run a  */ { hipY: 82, lean: 0.16, F: { x: 15, lift: 2 }, R: { x: -13, lift: 7 } },
  /* 3 run b  */ { hipY: 84, lean: 0.14, F: { x: 3, lift: 0 }, R: { x: -1, lift: 13 } },
  /* 4 run c  */ { hipY: 82, lean: 0.16, F: { x: -13, lift: 7 }, R: { x: 15, lift: 2 } },
  /* 5 run d  */ { hipY: 84, lean: 0.14, F: { x: -1, lift: 13 }, R: { x: 3, lift: 0 } },
  /* 6 rise   */ { hipY: 80, lean: 0.06, F: { x: 7, lift: 15 }, R: { x: -6, lift: 7 } },
  /* 7 fall   */ { hipY: 81, lean: 0.02, F: { x: 9, lift: 6 }, R: { x: -8, lift: 13 } },
  /* 8 dash   */ { hipY: 88, lean: 0.48, F: { x: 13, lift: 9 }, R: { x: -19, lift: 11 } },
  /* 9 kneel  */ { hipY: 101, lean: 0.55, F: { x: 12, lift: 0 }, R: { x: -12, lift: 0 } },
];

const LEG = { thigh: 21, shin: 21, ankle: 4 };
const TORSO = { w: 26, h: 30 };

const CARRY_F = { upper: 1.12, fore: 0.28 };
const CARRY_R = { upper: 0.95, fore: 0.18 };
const DASH_F = { upper: 1.75, fore: 0.75 };
const DASH_R = { upper: 2.2, fore: 1.7 };
const LIMP_F = { upper: 1.7, fore: 1.6 };
const LIMP_R = { upper: 1.6, fore: 1.5 };
/** Shoot layer: arm pull-back (px) and muzzle rise per frame. */
const SHOOT_RECOIL = [0, 4.5, 3.4, 2.2, 1, 0.3];
const SHOOT_LIFT = [0, -0.09, -0.07, -0.045, -0.02, -0.005];

const ARM = { upper: 17, fore: 19 };

function rotate(x: number, y: number, a: number): [number, number] {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
}

/** Torso-frame point (origin at the pelvis, −y up the spine) → cell px. */
function torsoPt(p: PhPose, x: number, y: number): [number, number] {
  const [rx, ry] = rotate(x, y, p.lean);
  return [FOOT_ANCHOR.x + rx, p.hipY + ry];
}

const ik = twoBoneResult();

function legJoints(p: PhPose, leg: LegKey, side: 1 | -1) {
  const hip = torsoPt(p, side * 2.5, 3);
  const ax = FOOT_ANCHOR.x + leg.x;
  const ay = FOOT_ANCHOR.y - leg.lift - LEG.ankle;
  // bend −1: knee forward (+x) for a leg pointing down.
  solveTwoBone(hip[0], hip[1], ax, ay, LEG.thigh, LEG.shin, -1, ik);
  return { hip, knee: [ik.ex, ik.ey] as [number, number], ankle: [ik.hx, ik.hy] as [number, number] };
}

function attachFor(p: PhPose): Record<AttachKey, AttachPoint> {
  const pt = (xy: [number, number], rot: number): AttachPoint => ({ x: xy[0], y: xy[1], rot });
  const lf = legJoints(p, p.F, 1);
  const lr = legJoints(p, p.R, -1);
  const shinRot = (l: ReturnType<typeof legJoints>) =>
    Math.atan2(l.ankle[1] - l.knee[1], l.ankle[0] - l.knee[0]) - Math.PI / 2;
  const mid = (a: [number, number], b: [number, number]): [number, number] => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  return {
    head: pt(torsoPt(p, 2, -40), p.lean * 0.5),
    chest: pt(torsoPt(p, 5, -19), p.lean),
    back: pt(torsoPt(p, -13, -21), p.lean),
    shoulderF: pt(torsoPt(p, 4, -27), p.lean),
    shoulderR: pt(torsoPt(p, -5, -28), p.lean),
    hip: pt(torsoPt(p, 0, 0), p.lean * 0.3),
    shinF: pt(mid(lf.knee, lf.ankle), shinRot(lf)),
    shinR: pt(mid(lr.knee, lr.ankle), shinRot(lr)),
    footF: pt([lf.ankle[0] + 2, lf.ankle[1] + LEG.ankle], 0),
    footR: pt([lr.ankle[0] + 2, lr.ankle[1] + LEG.ankle], 0),
  };
}

function metaFor(
  pose: number,
  armF = CARRY_F,
  armR = CARRY_R,
  aimWeight = 1,
  extra?: { recoil: number; lift: number },
): MechFrameMeta {
  const m: MechFrameMeta = {
    attach: attachFor(PH_POSES[pose]),
    armF: { ...armF },
    armR: { ...armR },
    aimWeight,
  };
  if (extra) {
    m.recoil = extra.recoil;
    m.lift = extra.lift;
  }
  return m;
}

function anim(poses: number[], fps: number, loop: boolean, meta: (pose: number, i: number) => MechFrameMeta): AnimSrc {
  return { frames: poses, fps, loop, meta: poses.map((p, i) => meta(p, i)) };
}

function placeholderAnims(): Record<AnimKey, AnimSrc> {
  const carry = (p: number) => metaFor(p);
  return {
    idle: anim([0, 0, 0, 0, 1, 1, 1, 1], 8, true, carry),
    run: anim([2, 2, 2, 3, 3, 3, 4, 4, 4, 5, 5, 5], 18, true, carry),
    jump: anim([6, 6, 6, 6, 7, 7, 7, 7], 12, false, carry),
    dash: anim([8, 8, 8, 8, 8, 8, 0, 0], 40, false, (p, i) => (i < 6 ? metaFor(p, DASH_F, DASH_R, 0.35) : carry(p))),
    shoot: anim([0, 0, 0, 0, 0, 0], 60, false, (p, i) =>
      metaFor(p, CARRY_F, CARRY_R, 1, { recoil: SHOOT_RECOIL[i], lift: SHOOT_LIFT[i] }),
    ),
    signature: anim([8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 0, 0], 20, false, (p, i) =>
      i < 10 ? metaFor(p, DASH_F, DASH_R, 0.5) : carry(p),
    ),
    hurt: anim([1, 1, 0, 0], 16, false, carry),
    death: anim([0, 1, 9, 9, 9, 9, 9, 9, 9, 9, 9, 9], 10, false, (p) => (p === 9 ? metaFor(p, LIMP_F, LIMP_R, 0) : metaFor(p, LIMP_F, LIMP_R, 0.3))),
  };
}

// ── Canvas painting ──

type Ctx = CanvasRenderingContext2D;

function rrect(c: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  c.beginPath();
  c.moveTo(x + r, y);
  c.lineTo(x + w - r, y);
  c.quadraticCurveTo(x + w, y, x + w, y + r);
  c.lineTo(x + w, y + h - r);
  c.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  c.lineTo(x + r, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - r);
  c.lineTo(x, y + r);
  c.quadraticCurveTo(x, y, x + r, y);
  c.closePath();
}

function limb(c: Ctx, pts: [number, number][], width: number, color: number): void {
  c.lineCap = 'round';
  c.lineJoin = 'round';
  c.beginPath();
  c.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) c.lineTo(pts[i][0], pts[i][1]);
  c.strokeStyle = css(COLORS.ink);
  c.lineWidth = width + 3;
  c.stroke();
  c.strokeStyle = css(color);
  c.lineWidth = width;
  c.stroke();
}

function drawLeg(c: Ctx, p: PhPose, leg: LegKey, side: 1 | -1, pal: Palette, rear: boolean): void {
  const j = legJoints(p, leg, side);
  const col = rear ? shade(pal.armor, 0.62) : pal.armor;
  limb(c, [j.hip, j.knee, j.ankle], 8, col);
  // Knee cap and foot.
  c.fillStyle = css(rear ? shade(pal.light, 0.6) : pal.light);
  c.beginPath();
  c.arc(j.knee[0], j.knee[1], 3.2, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = css(rear ? shade(pal.dark, 0.7) : pal.dark);
  c.strokeStyle = css(COLORS.ink);
  c.lineWidth = 1.5;
  rrect(c, j.ankle[0] - 6, j.ankle[1], 15, LEG.ankle + 0.5, 1.5);
  c.fill();
  c.stroke();
}

function drawBody(c: Ctx, p: PhPose, pal: Palette): void {
  drawLeg(c, p, p.R, -1, pal, true);
  c.save();
  c.translate(FOOT_ANCHOR.x, p.hipY);
  c.rotate(p.lean);
  c.strokeStyle = css(COLORS.ink);
  c.lineWidth = 2;
  // Backpack.
  c.fillStyle = css(pal.dark);
  rrect(c, -17, -33, 9, 22, 2);
  c.fill();
  c.stroke();
  // Pelvis.
  c.fillStyle = css(pal.dark);
  rrect(c, -9, -4, 18, 9, 2);
  c.fill();
  c.stroke();
  // Torso shell with a lit upper edge.
  c.fillStyle = css(pal.armor);
  rrect(c, -TORSO.w / 2, -TORSO.h - 3, TORSO.w, TORSO.h, 5);
  c.fill();
  c.stroke();
  c.fillStyle = css(pal.light);
  rrect(c, -TORSO.w / 2 + 3, -TORSO.h - 1, TORSO.w - 6, 4, 2);
  c.fill();
  // Chest plate seam + reactor housing.
  c.fillStyle = css(pal.dark);
  rrect(c, 1, -23, 10, 9, 2);
  c.fill();
  // Head.
  c.fillStyle = css(pal.armor);
  rrect(c, -6, -47, 15, 12, 3);
  c.fill();
  c.stroke();
  c.fillStyle = css(COLORS.ink);
  rrect(c, 2, -44, 8, 4, 1.5);
  c.fill();
  c.restore();
  drawLeg(c, p, p.F, 1, pal, false);
}

function drawGlow(c: Ctx, p: PhPose): void {
  c.save();
  c.translate(FOOT_ANCHOR.x, p.hipY);
  c.rotate(p.lean);
  c.fillStyle = '#ffffff';
  rrect(c, 3, -21.5, 6, 6, 2);
  c.fill();
  rrect(c, 3, -43.5, 6.5, 3, 1.2);
  c.fill();
  rrect(c, -15.5, -29, 2.5, 13, 1);
  c.fill();
  c.restore();
}

function canvasTexture(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  paint: (c: Ctx) => void,
): string {
  if (scene.textures.exists(key)) return key;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return key;
  paint(tex.context);
  tex.refresh();
  return key;
}

function sheetTexture(scene: Phaser.Scene, key: string, paint: (c: Ctx, p: PhPose) => void): void {
  if (scene.textures.exists(key)) return;
  const n = PH_POSES.length;
  const tex = scene.textures.createCanvas(key, CELL * n, CELL);
  if (!tex) return;
  const c = tex.context;
  PH_POSES.forEach((p, i) => {
    c.save();
    c.translate(i * CELL, 0);
    c.beginPath();
    c.rect(0, 0, CELL, CELL);
    c.clip();
    paint(c, p);
    c.restore();
    tex.add(i, 0, i * CELL, 0, CELL, CELL);
  });
  tex.refresh();
}

function armTexture(scene: Phaser.Scene, key: string, len: number, pal: Palette, hand: boolean): string {
  const pad = 5;
  const w = Math.ceil(len + pad * 2 + (hand ? 4 : 0));
  return canvasTexture(scene, key, w, 12, (c) => {
    limb(c, [[pad, 6], [pad + len, 6]], 6.5, pal.armor);
    c.fillStyle = css(pal.light);
    c.beginPath();
    c.arc(pad, 6, 3.3, 0, Math.PI * 2);
    c.fill();
    if (hand) {
      c.fillStyle = css(pal.dark);
      c.strokeStyle = css(COLORS.ink);
      c.lineWidth = 1.5;
      rrect(c, pad + len - 3, 2, 8, 8, 2);
      c.fill();
      c.stroke();
    }
  });
}

/** Placeholder rig in the mech's colours (frames, metadata, arm parts). */
export function buildPlaceholderRig(scene: Phaser.Scene, id: MechId): RigSrc {
  const pal = paletteFor(id);
  const bodyKey = `ph_mech_${id}_body`;
  const glowKey = `ph_mech_${id}_glow`;
  sheetTexture(scene, bodyKey, (c, p) => drawBody(c, p, pal));
  sheetTexture(scene, glowKey, (c, p) => drawGlow(c, p));
  const upperKey = armTexture(scene, `ph_mech_${id}_upper`, ARM.upper, pal, false);
  const foreKey = armTexture(scene, `ph_mech_${id}_fore`, ARM.fore, pal, true);
  const upperW = scene.textures.get(upperKey).getSourceImage().width || ARM.upper + 10;
  const foreW = scene.textures.get(foreKey).getSourceImage().width || ARM.fore + 14;
  return {
    bodyKey,
    glowKey,
    anims: placeholderAnims(),
    upperKey,
    foreKey,
    upperGlowKey: null,
    foreGlowKey: null,
    upperLen: ARM.upper,
    foreLen: ARM.fore,
    upperOrigin: { x: 5 / upperW, y: 0.5 },
    foreOrigin: { x: 5 / foreW, y: 0.5 },
    rearTint: 0x8a93a6,
    cell: CELL,
    footAnchor: { x: FOOT_ANCHOR.x, y: FOOT_ANCHOR.y },
    placeholder: true,
  };
}

/** Placeholder arm parts only (real body art whose arm textures are missing). */
export function placeholderArms(scene: Phaser.Scene, id: MechId): Pick<RigSrc, 'upperKey' | 'foreKey' | 'upperOrigin' | 'foreOrigin'> {
  const r = buildPlaceholderRig(scene, id);
  return { upperKey: r.upperKey, foreKey: r.foreKey, upperOrigin: r.upperOrigin, foreOrigin: r.foreOrigin };
}

/** Placeholder metadata for real sheets that ship without (enough) per-frame metadata. */
export function placeholderMeta(anim: AnimKey, i: number): MechFrameMeta {
  const a = placeholderAnims()[anim];
  return a.meta[Math.min(a.meta.length - 1, Math.max(0, i))];
}

/** Generic rifle in neutral steel with an emissive strip. */
export function placeholderWeapon(scene: Phaser.Scene): WeaponArt {
  const key = canvasTexture(scene, 'ph_weapon', 52, 16, (c) => {
    c.strokeStyle = css(COLORS.ink);
    c.lineWidth = 1.5;
    c.fillStyle = css(COLORS.steel3);
    rrect(c, 4, 4, 30, 8, 2); // receiver
    c.fill();
    c.stroke();
    c.fillStyle = css(COLORS.steel2);
    rrect(c, 32, 5.5, 18, 4, 1); // barrel
    c.fill();
    c.stroke();
    rrect(c, 8, 9, 6, 6, 1.5); // grip
    c.fill();
    c.stroke();
    c.fillStyle = css(COLORS.steel5);
    c.fillRect(7, 5, 22, 1.5);
  });
  const glowKey = canvasTexture(scene, 'ph_weapon_glow', 52, 16, (c) => {
    c.fillStyle = '#ffffff';
    c.fillRect(16, 7, 14, 2);
    c.fillRect(46, 6.5, 3, 3);
  });
  return {
    key,
    origin: { x: 11 / 52, y: 0.62 },
    muzzle: { x: 39, y: -2.4 },
    foregrip: { x: 21, y: 1 },
    length: 50,
    glowKey,
  };
}

/** Rigid module per slot for equipment whose art is missing. */
export function placeholderPieces(scene: Phaser.Scene, slot: EquipSlot): VisualPiece[] {
  const plate = (key: string, w: number, h: number) =>
    canvasTexture(scene, key, w, h, (c) => {
      c.strokeStyle = css(COLORS.ink);
      c.lineWidth = 1.5;
      c.fillStyle = css(COLORS.steel3);
      rrect(c, 1.5, 1.5, w - 3, h - 3, 3);
      c.fill();
      c.stroke();
      c.fillStyle = css(COLORS.steel5);
      c.fillRect(4, 3, w - 8, 1.5);
    });
  const glow = (key: string, w: number, h: number) =>
    canvasTexture(scene, key, w, h, (c) => {
      c.fillStyle = '#ffffff';
      c.fillRect(w / 2 - 2, h / 2 - 1, 4, 2.5);
    });
  const piece = (key: string, attach: AttachKey, layer: 'rear' | 'front', dx: number, dy: number, glowKey?: string): VisualPiece => ({
    key,
    attach,
    layer,
    dx,
    dy,
    rot: 0,
    originX: 0.5,
    originY: 0.5,
    followRot: true,
    glowKey,
  });
  switch (slot) {
    case 'chest': {
      const k = plate('ph_equip_chest', 20, 18);
      return [piece(k, 'chest', 'front', 1, 0, glow('ph_equip_chest_glow', 20, 18))];
    }
    case 'shoulders': {
      const k = plate('ph_equip_shoulder', 18, 13);
      const g = glow('ph_equip_shoulder_glow', 18, 13);
      return [piece(k, 'shoulderR', 'rear', -1, -5, g), piece(k, 'shoulderF', 'front', 1, -5, g)];
    }
    case 'back': {
      const k = plate('ph_equip_back', 12, 24);
      return [piece(k, 'back', 'rear', -4, 0, glow('ph_equip_back_glow', 12, 24))];
    }
    case 'legs': {
      const k = plate('ph_equip_legs', 9, 14);
      return [piece(k, 'shinR', 'rear', -4, 0), piece(k, 'shinF', 'front', -4, 0)];
    }
  }
}

/** Crosshair used when TEX.reticle is missing (white; tinted at runtime). */
export function placeholderReticle(scene: Phaser.Scene): string {
  return canvasTexture(scene, 'ph_reticle', 48, 48, (c) => {
    c.strokeStyle = '#ffffff';
    c.lineCap = 'round';
    c.lineWidth = 2.5;
    c.beginPath();
    c.arc(24, 24, 9, 0, Math.PI * 2);
    c.stroke();
    const tick = (x0: number, y0: number, x1: number, y1: number) => {
      c.beginPath();
      c.moveTo(x0, y0);
      c.lineTo(x1, y1);
      c.stroke();
    };
    tick(24, 4, 24, 12);
    tick(24, 36, 24, 44);
    tick(4, 24, 12, 24);
    tick(36, 24, 44, 24);
    c.fillStyle = '#ffffff';
    c.beginPath();
    c.arc(24, 24, 1.8, 0, Math.PI * 2);
    c.fill();
  });
}
